<template>
   <QCard id="onboarding-card">
      <QCardSection>
         <h1 class="text-h2 text-bold">Onboarding Checklist</h1>
      </QCardSection>

      <QCardSection>
         <!-- Header nav tabs -->
         <QStepper
            id="onboarding-checklist"
            ref="stepper"
            v-model="currentStep"
            header-nav
            color="primary"
            active-color="primary"
            done-color="positive"
            class="rounded-borders"
         >
            <!-- 1. Data Received Step -->
            <QStep
               :name="1"
               title="Install Web Tracker"
               :done="dataReceived"
               :icon="dataReceived ? 'mdi-check' : 'mdi-alert'"
               :header-nav="!dataReceived"
            >
               <NoDataReceived />
               <QBtn
                  label="I installed the tracker"
                  color="primary"
                  icon-right="mdi-arrow-right"
                  @click="currentStep = 2"
               />
            </QStep>

            <!-- 2. Reward Received Step -->
            <QStep
               :name="2"
               title="Set up Reward"
               :done="rewardReceived"
               :icon="rewardReceived ? 'mdi-check' : 'mdi-alert'"
               :header-nav="!rewardReceived"
            >
               <NoRewardReceived />
               <QBtn
                  label="I set up a reward"
                  color="primary"
                  icon-right="mdi-arrow-right"
                  @click="rewardNext"
               />
            </QStep>

            <!-- 3. Chrome Extension Step -->
            <QStep
               :name="3"
               title="Install Visual Editor"
               :done="ezbotExtensionInstalled"
               :icon="ezbotExtensionInstalled ? 'mdi-check' : 'mdi-alert'"
               :header-nav="!ezbotExtensionInstalled"
            >
               <ChromeExtensionNotInstalled />
            </QStep>

            <!-- 4. Variables Step -->
            <QStep
               :name="4"
               title="Create a Variable"
               :done="hasVariables"
               :icon="hasVariables ? 'mdi-check' : 'mdi-alert'"
               :header-nav="!hasVariables"
            >
               <NoVariablesConfigured />
            </QStep>

            <!-- 5. Waiting for Data Step -->
            <QStep
               :name="5"
               title="Onboarding Complete"
               icon="mdi-flag-checkered"
               active-icon="mdi-flag-checkered"
               :header-nav="false"
            >
               <OnboardingComplete />
            </QStep>
         </QStepper>
      </QCardSection>
   </QCard>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from "vue"
import { useOnboardingStore } from "@/stores/onboardingStore"
import { useProjectStore } from "@/stores/projectStore"
import NoDataReceived from "./stepper/NoDataReceived.vue"
import NoRewardReceived from "./stepper/NoRewardReceived.vue"
import ChromeExtensionNotInstalled from "./stepper/ChromeExtensionNotInstalled.vue"
import NoVariablesConfigured from "./stepper/NoVariablesConfigured.vue"
import OnboardingComplete from "./stepper/OnboardingComplete.vue"

// Access stores
const onboardingStore = useOnboardingStore()
const projectStore = useProjectStore()

// Stepper current step
const currentStep = ref(1)

const ezbotExtensionInstalled = ref(false)

// Computed properties for step completion status
const dataReceived = computed(() => onboardingStore.dataReceived === true)
const rewardReceived = computed(() => onboardingStore.rewardReceived === true)
const hasVariables = computed(
   () => projectStore.variables && projectStore.variables.length > 0,
)

// Set the current step based on progress
watch(
   [dataReceived, rewardReceived, ezbotExtensionInstalled, hasVariables],
   () => {
      if (!dataReceived.value) {
         currentStep.value = 1
      } else if (!rewardReceived.value) {
         currentStep.value = 2
      } else if (!ezbotExtensionInstalled.value) {
         currentStep.value = 3
      } else if (!hasVariables.value) {
         currentStep.value = 4
      } else {
         currentStep.value = 5
      }
   },
   { immediate: true },
)
const checkForExtension = () => {
   const extensionInstalled = document.getElementById(
      "ezbot-visual-editor-helper-script",
   )
   if (extensionInstalled) {
      ezbotExtensionInstalled.value = true
   }
}
checkForExtension()
const intervalId = setInterval(() => {
   checkForExtension()
}, 2000)
onUnmounted(() => {
   clearInterval(intervalId)
})
const rewardNext = () => {
   if (!ezbotExtensionInstalled.value) {
      currentStep.value = 3
   } else if (!hasVariables.value) {
      currentStep.value = 4
   } else {
      currentStep.value = 5
   }
}
</script>

<style lang="scss" scoped>
h1 {
   margin: 0;
}
#onboarding-checklist {
   min-height: 400px;
}
.step-content {
   margin-top: 20px;
   max-width: 100%;
   padding: 16px;
}
.q-stepper__step-content {
   padding-top: 24px;
   min-height: 300px;
}
</style>
